// MonthGrid.jsx — componente condiviso del design system (A3, ondata 30):
// griglia calendario mensile con gli STATI OBBLIGATORI del gestionale
// (regole UI 4):
//   loading → spinner + 'Caricamento…' (solo al primo giro senza dati)
//   error   → messaggio + bottone 'Riprova' (callback onRetry del chiamante)
//   data    → griglia 42 celle; con dati presenti il reload in corso/fallito
//             NON svuota la griglia (precedenza in month-grid-core
//             .selectMonthGridState, testata) — l'errore lo dichiara il
//             chiamante col suo banner (pattern D-A1.5/D-A2.2).
//   Il "mese vuoto" NON è uno stato dedicato: la griglia con le celle senza
//   eventi È la resa corretta (parità col calendario v1 di admin-module).
//
// API (pensata per riuso — primo consumatore CalendarioView amm-v3, secondo
// potenziale il calendario del Cruscotto dash):
//   cells        [{ iso, day, out, today }] — da buildCells del core
//   dow          etichette intestazione settimana (es. ['Lun',…,'Dom'])
//   eventsByDay  { iso → [ev] } — da groupByDay del core
//   maxPerCell   chip visibili per cella prima del link "+N altre" (default 3)
//   chip(ev)     → { label, bg, fg } (il chiamante decide colori/etichetta)
//   onChipClick(ev)                        click su un evento
//   moreLabel(nNascosti)                   testo del link (es. '+2 altre')
//   onMoreClick(iso, evs, anchorEl, ev)    click su "+N altre" (evs = TUTTI
//                                          gli eventi del giorno; ev = evento
//                                          DOM, per stopPropagation à la v1)
//   loading, error, onRetry
//   cells assenti/vuote = dati mai arrivati (loading/error a decidere);
//   cells presenti = stato data anche a reload in corso/fallito.
//
// I valori CSS delle celle sono VERBATIM dal calendario v1 (admin-module.js
// r.468-470: .amm-cell/.out/.today/.amm-ev) su classi proprie mg-* —
// componente auto-sufficiente in qualunque documento, precedente DataTable
// (dt-* con valori campionati dal v1). Il contract test month-grid.test.js
// pinna questi valori sul sorgente v1.

import React from 'react';
import { selectMonthGridState } from './month-grid-core.js';

const STYLE_ID = 'shell-ui-monthgrid-style';

function css() {
  return [
    '.mg-dow{display:grid;grid-template-columns:repeat(7,minmax(0,1fr));gap:6px;margin-bottom:6px}',
    '.mg-dow-cell{font-size:10px;text-transform:uppercase;color:#94a3b8;text-align:center;padding:4px 0}',
    '.mg-grid{display:grid;grid-template-columns:repeat(7,minmax(0,1fr));gap:6px}',
    // cella: valori VERBATIM v1 .amm-cell (r.468)
    '.mg-cell{background:#fff;border:1px solid #e2e8f0;border-radius:8px;min-height:84px;padding:5px 6px;min-width:0}',
    // fuori mese / oggi: VERBATIM v1 (r.469)
    '.mg-cell.mg-out{background:#f8fafc;opacity:.5}',
    '.mg-cell.mg-today{border-color:#0e7490;box-shadow:inset 0 0 0 1px #0e7490}',
    '.mg-day{font-size:11px;font-weight:500;color:#64748b}',
    '.mg-day.mg-today-n{color:#0e7490}',
    // chip evento: VERBATIM v1 .amm-ev (r.470)
    '.mg-ev{font-size:10px;padding:2px 5px;border-radius:4px;margin-bottom:3px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;cursor:pointer}',
    '.mg-more{font-size:9px;color:#94a3b8;cursor:pointer}',
    '.mg-more:hover{text-decoration:underline}',
    // stati obbligatori (stessa resa del DataTable: dt-state campionato)
    '.mg-state{background:#fff;border:1px solid #e2e8f0;border-radius:12px;padding:24px;text-align:center;color:#94a3b8;font-size:13px}',
    '.mg-loading{display:flex;align-items:center;justify-content:center;gap:10px}',
    '.mg-spin{width:18px;height:18px;border-radius:50%;border:2px solid #cffafe;border-top-color:#06b6d4;animation:mg-rot .8s linear infinite;display:inline-block}',
    '@keyframes mg-rot{to{transform:rotate(360deg)}}',
    '.mg-error{border-color:#fecaca;background:#fef2f2}',
    '.mg-errmsg{color:#b91c1c;font-size:13px;font-weight:600}',
    '.mg-retry{margin-top:12px;background:#0e7490;color:#fff;border:0;border-radius:8px;padding:8px 16px;font-weight:600;cursor:pointer;font-size:13px}',
  ].join('\n');
}

export function injectMonthGridStyle(doc) {
  if (!doc || doc.getElementById(STYLE_ID)) return;
  const st = doc.createElement('style');
  st.id = STYLE_ID;
  st.textContent = css();
  doc.head.appendChild(st);
}

export function MonthGrid(props) {
  const {
    cells, dow, eventsByDay, maxPerCell, chip, onChipClick,
    moreLabel, onMoreClick, loading, error, onRetry,
  } = props;
  const wrapRef = React.useRef(null);

  React.useEffect(() => {
    const el = wrapRef.current;
    if (el && el.ownerDocument) injectMonthGridStyle(el.ownerDocument);
  }, []);

  const byDay = eventsByDay || {};
  const max = maxPerCell == null ? 3 : maxPerCell;
  const state = selectMonthGridState({ loading, error, hasData: !!(cells && cells.length) });

  let body;
  if (state === 'loading') {
    body = (
      <div className="mg-state mg-loading" role="status">
        <span className="mg-spin" />
        <span>Caricamento…</span>
      </div>
    );
  } else if (state === 'error') {
    const msg = error && error.message ? error.message : String(error);
    body = (
      <div className="mg-state mg-error" role="alert">
        <div className="mg-errmsg">{'⚠ ' + msg}</div>
        {typeof onRetry === 'function' && (
          <button type="button" className="mg-retry" onClick={() => onRetry()}>Riprova</button>
        )}
      </div>
    );
  } else {
    body = (
      <>
        <div className="mg-dow">
          {(dow || []).map((d) => <div key={d} className="mg-dow-cell">{d}</div>)}
        </div>
        <div className="mg-grid">
          {(cells || []).map((c) => {
            const evs = byDay[c.iso] || [];
            const hidden = evs.length - max;
            return (
              <div key={c.iso} className={'mg-cell' + (c.out ? ' mg-out' : '') + (c.today ? ' mg-today' : '')} data-day={c.iso}>
                <div className={'mg-day' + (c.today ? ' mg-today-n' : '')}>{c.day}</div>
                {evs.slice(0, max).map((ev, i) => {
                  const ch = chip ? chip(ev) : { label: String(ev), bg: '#f1f5f9', fg: '#475569' };
                  return (
                    <div
                      key={i}
                      className="mg-ev"
                      style={{ background: ch.bg, color: ch.fg }}
                      title={ch.label}
                      onClick={onChipClick ? () => onChipClick(ev) : undefined}
                    >{ch.label}</div>
                  );
                })}
                {hidden > 0 && (
                  <div
                    className="mg-more"
                    data-day={c.iso}
                    onClick={onMoreClick ? (e) => onMoreClick(c.iso, evs, e.currentTarget, e) : undefined}
                  >{moreLabel ? moreLabel(hidden) : '+' + hidden}</div>
                )}
              </div>
            );
          })}
        </div>
      </>
    );
  }

  return <div className="mg-wrap" ref={wrapRef}>{body}</div>;
}
